import { useState, useEffect } from 'react';
import { Plus, Trash2, Calendar, Clock } from 'lucide-react';

export interface IncidentLogEntry {
  id: string;
  date: string;
  time: string;
  actif_touche: string;
  action_completee: string;
  resultat_souhaite: string;
}

interface IncidentLogJournalProps {
  value: IncidentLogEntry[] | string | null | undefined;
  onChange: (entries: IncidentLogEntry[]) => void;
  placeholder?: string;
}

export default function IncidentLogJournal({ value, onChange, placeholder }: IncidentLogJournalProps) {
  const getEntries = (): IncidentLogEntry[] => {
    if (!value) return [];
    if (typeof value === 'string') {
      if (value.trim() === '') return [];
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  };

  const [entries, setEntries] = useState<IncidentLogEntry[]>(getEntries());

  useEffect(() => {
    if (!value) {
      setEntries([]);
      return;
    }
    if (typeof value === 'string') {
      if (value.trim() === '') {
        setEntries([]);
        return;
      }
      try {
        const parsed = JSON.parse(value);
        setEntries(Array.isArray(parsed) ? parsed : []);
      } catch {
        setEntries([]);
      }
    } else if (Array.isArray(value)) {
      setEntries(value);
    } else {
      setEntries([]);
    }
  }, [value]);

  const handleEntriesChange = (newEntries: IncidentLogEntry[]) => {
    setEntries(newEntries);
    onChange(newEntries);
  };

  const addEntry = () => {
    const now = new Date();
    const newEntry: IncidentLogEntry = {
      id: `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      date: now.toISOString().split('T')[0],
      time: now.toTimeString().split(' ')[0].substring(0, 5),
      actif_touche: '',
      action_completee: '',
      resultat_souhaite: '',
    };
    handleEntriesChange([...entries, newEntry]);
  };

  const removeEntry = (id: string) => {
    handleEntriesChange(entries.filter((entry) => entry.id !== id));
  };

  const updateEntry = (id: string, field: keyof IncidentLogEntry, newValue: string) => {
    handleEntriesChange(
      entries.map((entry) => (entry.id === id ? { ...entry, [field]: newValue } : entry))
    );
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Journal de la gestion de l'incident</h3>
        <p className="text-xs text-gray-500 italic">
          ITIL 4 et NIST CSF 2.0 (RS.CO-2) : Chronologie complète de toutes les actions pour audit, analyse post-incident et amélioration continue.
        </p>
      </div>

      {entries.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <p className="text-xs text-gray-500 mb-4">{placeholder || 'Aucune entrée dans le journal'}</p>
          <button
            type="button"
            onClick={addEntry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-bz-blue text-white text-xs font-semibold rounded-lg hover:bg-bz-blue/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter une entrée
          </button>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-300 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-300">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Date et heure</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Actif TI touché</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Action complétée</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Résultat souhaité</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <input
                            type="date"
                            value={entry.date}
                            onChange={(e) => updateEntry(entry.id, 'date', e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <input
                            type="time"
                            value={entry.time}
                            onChange={(e) => updateEntry(entry.id, 'time', e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="text"
                        value={entry.actif_touche}
                        onChange={(e) => updateEntry(entry.id, 'actif_touche', e.target.value)}
                        placeholder="Ex: Serveur SRV-01"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="text"
                        value={entry.action_completee}
                        onChange={(e) => updateEntry(entry.id, 'action_completee', e.target.value)}
                        placeholder="Ex: Coupure d'internet"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="text"
                        value={entry.resultat_souhaite}
                        onChange={(e) => updateEntry(entry.id, 'resultat_souhaite', e.target.value)}
                        placeholder="Ex: Isolation complète"
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => removeEntry(entry.id)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        title="Supprimer cette entrée"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={addEntry}
              className="inline-flex items-center gap-2 px-4 py-2 bg-bz-blue text-white text-xs font-semibold rounded-lg hover:bg-bz-blue/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter une entrée
            </button>
          </div>
        </>
      )}
    </div>
  );
}
